import React, { useState } from 'react';
import ProfilePhotoUpload from '../../ProfilePhotoUpload/ProfilePhotoUpload';

const GENDER_OPTIONS = [
  { value: '', label: 'Select gender' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
];

const ETHNICITY_OPTIONS = [
  { value: '', label: 'Select ethnicity' },
  { value: 'african', label: 'African' },
  { value: 'asian', label: 'Asian' },
  { value: 'caribbean', label: 'Caribbean' },
  { value: 'caucasian', label: 'Caucasian' },
  { value: 'hispanic', label: 'Hispanic/Latino' },
  { value: 'middle-eastern', label: 'Middle Eastern' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'other', label: 'Other' },
];

const AboutMeStep = ({ formData, onNext, onBack }) => {
  const [localData, setLocalData] = useState({
    dateOfBirth: formData.dateOfBirth || '',
    gender: formData.gender || '',
    ethnicity: formData.ethnicity || '',
    profilePicture: formData.profilePicture || '',
  });
  const [errors, setErrors] = useState({});

  // Calculate max date (today - no future dates)
  const today = new Date().toISOString().split('T')[0];

  // Calculate min date (reasonable age limit - 100 years ago)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);
  const minDateStr = minDate.toISOString().split('T')[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhotoChange = (url) => {
    setLocalData((prev) => ({ ...prev, profilePicture: url }));
    if (errors.profilePicture) {
      setErrors((prev) => ({ ...prev, profilePicture: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!localData.dateOfBirth) {
      newErrors.dateOfBirth = 'Birthday is required';
    } else {
      const birthDate = new Date(localData.dateOfBirth);
      const now = new Date();
      if (birthDate > now) {
        newErrors.dateOfBirth = 'Birthday cannot be in the future';
      }
      // Check if user is at least 13 years old
      const age = Math.floor((now - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      if (age < 13) {
        newErrors.dateOfBirth = 'You must be at least 13 years old';
      }
    }

    if (!localData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // Ethnicity and profile photo are optional - no validation required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      onNext(localData);
    }
  };

  return (
    <div className="wizard-step">
      <h2 className="step-title">A Little About You</h2>
      <p className="step-subtitle">Help us personalize your experience</p>

      <form onSubmit={handleSubmit}>
        {/* Profile Photo (Optional) */}
        <div className="wizard-field">
          <ProfilePhotoUpload
            value={localData.profilePicture}
            onChange={handlePhotoChange}
            label="Profile Photo (Optional)"
            error={errors.profilePicture}
          />
        </div>

        {/* Birthday */}
        <div className="wizard-field">
          <label htmlFor="dateOfBirth">
            Birthday
            <span className="required-asterisk">*</span>
          </label>
          <input
            type="date"
            id="dateOfBirth"
            name="dateOfBirth"
            className={`wizard-input ${errors.dateOfBirth ? 'has-error' : ''}`}
            value={localData.dateOfBirth}
            onChange={handleChange}
            max={today}
            min={minDateStr}
          />
          {errors.dateOfBirth && <div className="field-error">{errors.dateOfBirth}</div>}
        </div>

        {/* Gender & Ethnicity Row */}
        <div className="wizard-row">
          <div className="wizard-field">
            <label htmlFor="gender">
              Gender
              <span className="required-asterisk">*</span>
            </label>
            <select
              id="gender"
              name="gender"
              className={`wizard-select ${errors.gender ? 'has-error' : ''}`}
              value={localData.gender}
              onChange={handleChange}
            >
              {GENDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.gender && <div className="field-error">{errors.gender}</div>}
          </div>

          <div className="wizard-field">
            <label htmlFor="ethnicity" className="ethnicity-label">
              Ethnicity
              <span className="ethnicity-info-icon" data-tooltip="Help us understand how beautifully diverse our dance community is. This is optional and is used only for anonymous community statistics. Thank you for telling us more about yourself!">
                <i className="bi bi-info-circle"></i>
              </span>
            </label>
            <select
              id="ethnicity"
              name="ethnicity"
              className="wizard-select"
              value={localData.ethnicity}
              onChange={handleChange}
            >
              {ETHNICITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Navigation */}
        <div className="wizard-navigation">
          <button type="button" className="wizard-btn wizard-btn-back" onClick={onBack}>
            <i className="bi bi-arrow-left"></i>
            Back
          </button>
          <button type="submit" className="wizard-btn wizard-btn-next">
            Next
            <i className="bi bi-arrow-right"></i>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutMeStep;
